import { diContainer } from '@/coreContainer/container';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { IError } from '@/coreInfrastructureModule/interfaces/IError';

/**
 * フィールド値を取得するUseCaseの型
 */
type GetFieldUseCase = {
  execute(value: string): Promise<INetworkResult<string | undefined, IError>>;
};

/**
 * リゾルバの定義
 */
type PairResolver = {
  useCase: GetFieldUseCase;
  targetKey: string;
};

const MasterPairResolverKeys = [
  'businessNumber',
  'businessName',
  'warehouseNumber',
  'warehouseName',
  'supplierNumber',
  'supplierName',
  'customerNumber',
  'customerName',
  'salesDestinationNumber',
  'salesDestinationName',
] as const;

/**
 * マスタキー名
 */
export type MasterPairResolverKey = (typeof MasterPairResolverKeys)[number];

/**
 * マスタの対応する値を取得するサービス
 */
export class MasterPairResolverService {
  private static masterPairResolvers: Readonly<Record<MasterPairResolverKey, PairResolver>> = {
    businessNumber: { useCase: diContainer.getBusinessNameUseCase, targetKey: 'businessName' },
    businessName: { useCase: diContainer.getBusinessNumberUseCase, targetKey: 'businessNumber' },
    warehouseNumber: { useCase: diContainer.getWarehouseNameUseCase, targetKey: 'warehouseName' },
    warehouseName: { useCase: diContainer.getWarehouseNumberUseCase, targetKey: 'warehouseNumber' },
    supplierNumber: { useCase: diContainer.getSupplierNameUseCase, targetKey: 'supplierName' },
    supplierName: { useCase: diContainer.getSupplierNumberUseCase, targetKey: 'supplierNumber' },
    customerNumber: { useCase: diContainer.getCustomerNameUseCase, targetKey: 'customerName' },
    customerName: { useCase: diContainer.getCustomerNumberUseCase, targetKey: 'customerNumber' },
    salesDestinationNumber: { useCase: diContainer.getSalesDestinationNameUseCase, targetKey: 'salesDestinationName' },
    salesDestinationName: { useCase: diContainer.getSalesDestinationNumberUseCase, targetKey: 'salesDestinationNumber' },
  };

  /**
   * マスタ項目のペアを取得する
   * @param key - キー
   * @param value - キーの値
   * @param resolvers - リゾルバ
   */
  public static resolveMasterPairValue = async (
    key: string,
    value: string,
    resolvers: Readonly<Record<string, PairResolver>> = MasterPairResolverService.masterPairResolvers
  ) => {
    if (value === '') return undefined;

    const resolver = resolvers[key];
    if (!resolver) return undefined;

    const result = await resolver.useCase.execute(value);

    if (result.status.type !== 'success') return undefined;
    if (result.status.data === undefined) return undefined;

    return {
      targetKey: resolver.targetKey,
      value: result.status.data,
    };
  };
}
